import { ARTWORK } from "./artwork";
import {
  Atom, BarChart3, Brain, Building2, Cpu, Crown, Gem, Globe, Landmark, Link2,
  Orbit, Rocket, Settings, Smartphone, Users, UsersRound, Zap,
} from "lucide-react";

// ─── SINGLE SOURCE OF TRUTH FOR THE $10M UNLOCK ───────────────────────────────
export const UNLOCK_MARKET_CAP = 10_000_000;

export const TOKEN_REFRESH_MS = 30_000;

export const BRAND = {
  name: "KINGBULLISH",
  ticker: "$KBULL",
  chain: "SOLANA",
  tagline: "INVEST IN THE FUTURE",
  supporting: ["SAME VISION.", "BIGGER HORIZONS."],
};

// All external links live here. Replace "#" with real URLs.
export const LINKS = {
  buy: "/buy",
  whitepaper: "#",
  privacy: "#",
  terms: "#",
  contact: "#",
};

export const SOCIAL_LINKS = {
  x: "https://x.com/kingbullish_?s=11",
  telegram: "https://t.me/KingBullish09",
  reddit: "https://www.reddit.com/u/semtex330/s/xUf9YP0h6P",
  discord: "https://discord.gg/MmvsUbxsf",
  youtube: "https://www.youtube.com/@KING_BULLISH",
  tiktok: "https://www.tiktok.com/@kingbullish?_r=1&_t=ZG-99hb3vpAgYz",
  instagram: "https://www.instagram.com/kingbullish_?stkn=MWF6ejE4cjVzY3Zxaw%3D%3D&utm_source=qr",
};

export const SOCIALS = [
  { id: "x", label: "X" },
  { id: "telegram", label: "Telegram" },
  { id: "reddit", label: "Reddit" },
  { id: "discord", label: "Discord" },
  { id: "youtube", label: "YouTube" },
  { id: "tiktok", label: "TikTok" },
  { id: "instagram", label: "Instagram" },
].map((social) => ({ ...social, href: SOCIAL_LINKS[social.id] }));

export const NAV = [
  { id: "home", label: "HOME", target: "home" },
  { id: "chart", label: "GRAPHIQUE", target: "chart" },
  { id: "roadmap", label: "ROADMAP", target: "roadmap" },
  { id: "ecosystem", label: "ECOSYSTEM", target: "ecosystem" },
  { id: "community", label: "COMMUNITY", target: "community" },
  { id: "whitepaper", label: "WHITEPAPER", href: LINKS.whitepaper, external: true },
];

export const HERO_TAGS = ["AI", "QUANTUM", "SEMICONDUCTORS", "TECHNOLOGY", "REAL-WORLD UTILITY"];

export const HERO_COPY =
  "Bridging AI, quantum, semiconductors and real-world utility on Solana. Driven by the community. More than a token. A global movement.";

export const HERO_FEATURES = [
  { id: "real-utility", title: "REAL UTILITY", desc: "Products with real value", icon: BarChart3 },
  { id: "community", title: "COMMUNITY", desc: "Driven by the people", icon: Users },
  { id: "global-vision", title: "GLOBAL VISION", desc: "Bigger horizons", icon: Globe },
  { id: "long-term-impact", title: "LONG-TERM IMPACT", desc: "A brighter tomorrow", icon: Zap },
];

export const TECH_STACK = [
  { label: "AI", icon: Brain },
  { label: "QUANTUM", icon: Atom },
  { label: "SEMICONDUCTORS", icon: Cpu },
  { label: "TECHNOLOGY", icon: Rocket },
  { label: "REAL-WORLD ASSETS", icon: Building2 },
];

export const ROADMAP = [
  { id: "100k", cap: 100_000, title: "FOUNDATION", color: "#00FF66", icon: Rocket,
    items: ["Token Launch", "Website Live", "Community Setup", "Social Channels", "CMC & CG Listings"] },
  { id: "250k", cap: 250_000, title: "GROWTH", color: "#00FF66", icon: Users,
    items: ["Marketing Phase 1", "Community Building", "Memes & Awareness", "Listings"] },
  { id: "500k", cap: 500_000, title: "EXPANSION", color: "#00F0FF", icon: BarChart3,
    items: ["DEX Listings", "Liquidity Lock", "Strategic Partnerships", "More Visibility"] },
  { id: "1m", cap: 1_000_000, title: "COMMUNITY POWER", color: "#0072FF", icon: Globe,
    items: ["Larger Community", "Influencer Deals", "Global Reach"] },
  { id: "10m", cap: UNLOCK_MARKET_CAP, title: "MAJOR ECOSYSTEM UNLOCK", color: "#FFD700", major: true,
    products: [
      { name: "KING NFTs", sub: "EXCLUSIVE COLLECTION", icon: Crown,
        items: ["Unique NFTs", "Utility & Rewards", "Real-World Benefits"] },
      { name: "KINGBULLISH SMART PORTFOLIOS", sub: "AI · QUANTUM · SEMICONDUCTORS · TECH", icon: BarChart3,
        items: ["Thematic Portfolios", "AI-Powered Insights", "Next-Gen Finance"] },
      { name: "KINGBULLISH APP", sub: "NEXT-GENERATION MOBILE APP", icon: Smartphone,
        items: ["Trade", "Track", "Earn"] },
    ] },
  { id: "25m", cap: 25_000_000, title: "ECOSYSTEM EXPANSION", color: "#A855F7", icon: Settings,
    items: ["Advanced Trading Tools", "Whale Tracker", "Mobile App Full Release", "More Assets"] },
  { id: "50m", cap: 50_000_000, title: "GLOBAL MARKETS", color: "#FF2D95", icon: Orbit,
    items: ["CEX Listings", "Global Partnerships", "Institutional Tools", "Metaverse Integration"] },
  { id: "100m", cap: 100_000_000, title: "INSTITUTIONAL PHASE", color: "#FF3355", icon: Landmark,
    items: ["Tokenized Assets", "Real Estate", "Gold, Stocks & RWA", "Funds & Institutions"] },
  { id: "250m", cap: 250_000_000, title: "REAL WORLD", color: "#FF6B8A", icon: Link2,
    items: ["Real-World Adoption", "Payment Solutions", "Staking Cards", "Mass Integration"] },
  { id: "500m", cap: 500_000_000, title: "MASS ADOPTION", color: "#4DA3FF", icon: UsersRound,
    items: ["Worldwide Expansion", "More Products", "More Utilities", "A Stronger Community"] },
  { id: "1b", cap: 1_000_000_000, label: "$1B+", title: "THE FUTURE", color: "#00F0FF", icon: Gem,
    items: ["Global Adoption", "Financial Revolution", "A Better Tomorrow"] },
];

export const ECOSYSTEM_PRODUCTS = [
  { id: "king-nfts", name: "KING NFTs", subtitle: "EXCLUSIVE COLLECTION", tagline: "Collect. Earn. Access.",
    image: ARTWORK.nfts, color: "#00FF66" },
  { id: "smart-portfolios", name: "KINGBULLISH SMART PORTFOLIOS", subtitle: "AI · QUANTUM · SEMICONDUCTORS · TECH",
    tagline: "Invest in the next era.", image: ARTWORK.portfolios, color: "#00F0FF" },
  { id: "app", name: "KINGBULLISH APP", subtitle: "TRADE. TRACK. EARN.", tagline: "All in one. On Solana.",
    image: ARTWORK.app, color: "#00FF66" },
  { id: "kb-ai", name: "KB AI", subtitle: "NEXT-GEN ANALYTICS", tagline: "Smarter decisions.",
    image: ARTWORK.kbAi, color: "#A855F7" },
];

export const THEMATIC_PORTFOLIOS = [
  { id: "ai", name: "AI", image: ARTWORK.ai, color: "#00FF66" },
  { id: "quantum", name: "QUANTUM", image: ARTWORK.quantum, color: "#A855F7" },
  { id: "semiconductors", name: "SEMICONDUCTORS", image: ARTWORK.semiconductors, color: "#00F0FF" },
  { id: "tech", name: "TECH", image: ARTWORK.tech, color: "#0072FF" },
  { id: "clean-energy", name: "CLEAN ENERGY", image: ARTWORK.energy, color: "#00FF66" },
  { id: "space-defense", name: "SPACE & DEFENSE", image: ARTWORK.spaceDefense, color: "#4DA3FF" },
];

export const COMMUNITY = {
  countries: null, // no verified figure before launch — footer shows "—"
};
